// I originally tried dynamically loading this module but this omitted the traces that were part of the initial
// page load (since this package dynamically loaded after those requests had fired)
import { PANTHER_CONFIG } from 'Source/constants';
import { datadogRum } from '@datadog/browser-rum';
import urls from 'Source/urls';
import { AppConfig } from 'Generated/schema';

type PantherEPDTeam =
  | 'automation'
  | 'connections'
  | 'alerting'
  | 'processing'
  | 'detections'
  | 'investigations';

// Based on https://www.notion.so/pantherlabs/6a8ac232231e4c1c9a5802be1fc3c9f9?v=04e80f17b749414ba0e6b0b60c3cc8e0
//
// Order matters so please don't re-arrange the conditionals. I'm intentionally not putting them in a map/array since
// the ordering of the checks matters in order to minimize the needed conditions
export const urlToPantherEPDTeam = (url: string): PantherEPDTeam => {
  if (url.includes(urls.configure.cloudAccounts.home())) {
    return 'connections';
  }

  if (url.includes(urls.settings.home())) {
    return 'automation';
  }

  if (url.includes(urls.alerts.home())) {
    return 'alerting';
  }

  if (url.includes(urls.build.home()) || url.includes(urls.investigate.cloudResources.home())) {
    return 'detections';
  }

  // Log sources are tricky to route, but for now we'll tag team processing until connections gets up to speed with
  // the `source-api`
  if (
    url.includes(urls.configure.logSources.home()) ||
    url.includes(urls.configure.schemas.home()) ||
    url.includes(urls.configure.enrichmentProviders.home()) ||
    url.includes(urls.configure.alertDestinations.home())
  ) {
    return 'processing';
  }

  if (url.includes(urls.investigate.home())) {
    return 'investigations';
  }

  // Fallback for any page that's forgotten (we purposefully ignore auth-pages since they don't matter for tracing)
  return 'automation';
};

export const initializeTracing = (appConfig: AppConfig) => {
  // We intentionally don't enable Browser RUM during local development to save on costs
  if (appConfig.datadogRumEnabled && window.location.hostname !== 'localhost') {
    // Sets the default tags that each trace/session should have on Datadog
    // Aligned with: deployments/pulumi/src/components/datadog_aws.py#default_tags

    // https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#global-context
    datadogRum.setGlobalContext({
      aws_account: appConfig.accountId,
      aws_account_alias: String(appConfig.awsAccountAlias ?? '').toLowerCase(),
      aws_org_id: appConfig.awsOrgId,
      customer_name: String(appConfig.customerName ?? '').toLowerCase(),
      data_lake: appConfig.pantherDataLakeType,
      sales_phase: appConfig.salesPhase,
      screen: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    });

    // https://docs.datadoghq.com/real_user_monitoring/browser/#configuration
    datadogRum.init({
      applicationId: appConfig.datadogWebApplicationId,
      clientToken: appConfig.datadogWebClientToken,
      service: appConfig.datadogWebServiceName,
      env: PANTHER_CONFIG.ENVIRONMENT || 'dev',
      version: PANTHER_CONFIG.PANTHER_VERSION,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackFrustrations: true,
      trackResources: true,
      defaultPrivacyLevel: 'mask',
      allowedTracingUrls: [new URL(PANTHER_CONFIG.WEB_APPLICATION_GRAPHQL_API_ENDPOINT).origin],
      enableExperimentalFeatures: ['feature_flags'],
      beforeSend: event => {
        // At the moment of writing, this is the recommended way of adding "custom stuff" to the autogenerated "view"
        // event, since the view's context is read-only and cannot be modified
        // https://github.com/DataDog/browser-sdk/issues/1659#issuecomment-1198998421
        datadogRum.addRumGlobalContext('team', urlToPantherEPDTeam(event.view.url));
      },
    });

    datadogRum.startSessionReplayRecording();
  }
};
